import { Button, Divider, Input, Form, Select, Flex } from "antd";
import { useTranslation } from "react-i18next";

import { INPUT_MAX_LENGTH } from "@/constants/Fields";
import { templateCategories } from "@/constants/TemplateCategories";
import { useUpdate } from "@/hooks/queries";
import { useApiErrorHandler } from "@/modules/api-errors";
import { validationErrorParser } from "@/modules/form-validation";
import { useSidePanel } from "@/modules/side-panel";
import { createRandomId } from "@/utilities/id";

import { TreeModifier } from "./TreeModifier";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;
const { useForm } = Form;

const SectionAdd = ({ template, layoutIndex }) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { closeSidePanel } = useSidePanel();
  const { onError } = useApiErrorHandler();

  const mutation = useUpdate("interventionTemplates", template.id);

  const categories = templateCategories.map((category) => ({
    label: t(`interventionTemplate:category.${category}`),
    value: category,
  }));

  function onSave() {
    const updatedTemplate = TreeModifier.findAndAppendChild(
      template,
      "templates",
      (section) =>
        (!layoutIndex && !section.layout_index) ||
        section.layout_index == layoutIndex,
      {
        name: form.getFieldValue("name"),
        description: form.getFieldValue("description") ?? "",
        layout_index: createRandomId(),
        type: "inspection", // TODO: Remove this field when no longer need to support AppQt/vuejs
        flags: template.flags,
        category: form.getFieldValue("category"),
        templates: [],
      }
    );

    mutation.mutate(
      { templates: updatedTemplate.templates },
      {
        onSuccess: function () {
          onClose();
        },
        onError,
      }
    );
  }

  function validationErrors() {
    return mutation.isError
      ? validationErrorParser(
          mutation.error,
          ["name", "description", "category"],
          "others"
        )
      : {};
  }

  function validation(item) {
    return {
      validateStatus: validationErrors()[item] ? "error" : "status",
      help: validationErrors()[item],
    };
  }

  function onClose() {
    form.resetFields();
    closeSidePanel();
  }

  return (
    <Form form={form} layout="vertical" onFinish={onSave}>
      <Form.Item
        name="name"
        label={t("interventionTemplate:name.label")}
        rules={[
          {
            required: true,
          },
        ]}
        {...validation("name")}
      >
        <Input
          data-testid="name"
          placeholder={t("interventionTemplate:name.label")}
          maxLength={INPUT_MAX_LENGTH}
          showCount
        />
      </Form.Item>
      <Form.Item
        name="description"
        label={t("interventionTemplate:description:label")}
        {...validation("description")}
      >
        <Input
          data-testid="description"
          placeholder={t("interventionTemplate:description:label")}
        />
      </Form.Item>
      <Form.Item
        name="category"
        label={t("interventionTemplate:form.category.label")}
        rules={[
          {
            required: true,
          },
        ]}
        {...validation("category")}
      >
        <Select placeholder={t("interventionTemplate:form.category.label")}>
          {categories.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Divider />
      <Form.Item>
        <Flex gap="small">
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            name="save"
            data-testid="save"
            htmlType="submit"
          >
            {t("global:save")}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  );
};

export default SectionAdd;
